import React, { useState, useCallback } from "react";
import axios from "axios";
import confirmAction from "../../utils/confirm";

const getLocale = () => window.location.pathname.split("/")[1];
const csrfToken = () => document.querySelector("[name=csrf-token]")?.content;

const axiosConfig = () => ({
  headers: {
    "X-CSRF-TOKEN": csrfToken(),
  },
});

const axiosMultipart = () => ({
  headers: {
    "X-CSRF-TOKEN": csrfToken(),
    "Content-Type": "multipart/form-data",
  },
});

// Tab editor component
const TabEditor = ({ tab, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const [label, setLabel] = useState(tab.label);
  const [content, setContent] = useState(tab.content);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(tab.carousel_images || []);
  const locale = getLocale();

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `/${locale}/admin/about_us/tabs/${tab.id}`,
        { label, content },
        axiosConfig()
      );
      onUpdate({ ...tab, label, content });
    } catch (e) {
      console.error("Failed to save tab:", e);
    }
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append("images[]", f));
    try {
      const res = await axios.post(
        `/${locale}/admin/about_us/tabs/${tab.id}/carousel_images`,
        formData,
        axiosMultipart()
      );
      setImages(res.data.carousel_images);
    } catch (e) {
      console.error("Failed to upload images:", e);
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDeleteImage = (imageId) => {
    confirmAction(async () => {
      try {
        await axios.delete(
          `/${locale}/admin/about_us/tabs/${tab.id}/carousel_images/${imageId}`,
          axiosConfig()
        );
        setImages(images.filter((img) => img.id !== imageId));
      } catch (e) {
        console.error("Failed to delete image:", e);
      }
    });
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Tab: {tab.label}</h5>
          <div>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isFirst} onClick={onMoveUp}>&#9650;</button>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isLast} onClick={onMoveDown}>&#9660;</button>
            <button className="btn btn-sm btn-danger" onClick={() => confirmAction(() => onDelete(tab.id))}>Delete</button>
          </div>
        </div>

        <div className="form-group">
          <label>Label</label>
          <input type="text" className="form-control" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>

        <div className="form-group mt-2">
          <label>Content (HTML)</label>
          <textarea className="form-control" rows="8" value={content} onChange={(e) => setContent(e.target.value)} />
        </div>

        <button className="btn btn-primary mt-2" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Tab"}
        </button>

        <hr />
        <h6>Carousel Images</h6>
        <div className="row mb-2">
          {images.map((img) => (
            <div key={img.id} className="col-3 mb-2 position-relative">
              <img src={img.url} alt={img.filename} className="img-fluid rounded" style={{ maxHeight: 120 }} />
              <button
                className="btn btn-sm btn-danger position-absolute"
                style={{ top: 4, right: 20 }}
                onClick={() => handleDeleteImage(img.id)}
              >&times;</button>
            </div>
          ))}
        </div>
        <div className="form-group">
          <input type="file" className="form-control-file" accept="image/*" multiple onChange={handleImageUpload} disabled={uploading} />
          {uploading && <small className="text-muted">Uploading...</small>}
        </div>
      </div>
    </div>
  );
};

// Achievement editor component
const AchievementEditor = ({ achievement, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const [label, setLabel] = useState(achievement.label);
  const [count, setCount] = useState(achievement.count);
  const [saving, setSaving] = useState(false);
  const [icon, setIcon] = useState(achievement.icon);
  const locale = getLocale();

  const handleSave = async () => {
    setSaving(true);
    const formData = new FormData();
    formData.append("label", label);
    formData.append("count", count);
    try {
      await axios.patch(
        `/${locale}/admin/about_us/achievements/${achievement.id}`,
        formData,
        axiosMultipart()
      );
      onUpdate({ ...achievement, label, count });
    } catch (e) {
      console.error("Failed to save achievement:", e);
    }
    setSaving(false);
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(true);
    const formData = new FormData();
    formData.append("icon", file);
    formData.append("label", label);
    formData.append("count", count);
    try {
      const res = await axios.patch(
        `/${locale}/admin/about_us/achievements/${achievement.id}`,
        formData,
        axiosMultipart()
      );
      // Reload to get new icon URL
      const dataRes = await axios.get(`/${locale}/admin/about_us.json`, axiosConfig());
      const updated = dataRes.data.achievements.find((a) => a.id === achievement.id);
      if (updated) setIcon(updated.icon);
    } catch (e) {
      console.error("Failed to upload icon:", e);
    }
    setSaving(false);
    e.target.value = "";
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Achievement: {achievement.label}</h5>
          <div>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isFirst} onClick={onMoveUp}>&#9650;</button>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isLast} onClick={onMoveDown}>&#9660;</button>
            <button className="btn btn-sm btn-danger" onClick={() => confirmAction(() => onDelete(achievement.id))}>Delete</button>
          </div>
        </div>

        <div className="row">
          <div className="col-md-2 text-center">
            {icon && icon.url ? (
              <img src={icon.url} alt={icon.filename} style={{ maxHeight: 80 }} className="mb-2" />
            ) : (
              <div className="text-muted mb-2">No icon</div>
            )}
            <input type="file" className="form-control-file" accept="image/*" onChange={handleIconUpload} />
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>Label</label>
              <input type="text" className="form-control" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>Count</label>
              <input type="number" className="form-control" value={count} min="0" onChange={(e) => setCount(parseInt(e.target.value, 10) || 0)} />
            </div>
          </div>
        </div>

        <button className="btn btn-primary mt-2" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Achievement"}
        </button>
      </div>
    </div>
  );
};

const AboutUsSettings = ({ initialData, locale }) => {
  const [tabs, setTabs] = useState(initialData.tabs || []);
  const [achievements, setAchievements] = useState(initialData.achievements || []);
  const [addingTab, setAddingTab] = useState(false);
  const [addingAchievement, setAddingAchievement] = useState(false);
  const [newTabLabel, setNewTabLabel] = useState("");
  const [newAchLabel, setNewAchLabel] = useState("");

  const reorderTabs = useCallback(async (newTabs) => {
    setTabs(newTabs);
    const ids = newTabs.map((t) => t.id);
    try {
      await axios.patch(`/${locale}/admin/about_us/tabs/reorder`, { ids }, axiosConfig());
    } catch (e) {
      console.error("Failed to reorder tabs:", e);
    }
  }, [locale]);

  const reorderAchievements = useCallback(async (newAchs) => {
    setAchievements(newAchs);
    const ids = newAchs.map((a) => a.id);
    try {
      await axios.patch(`/${locale}/admin/about_us/achievements/reorder`, { ids }, axiosConfig());
    } catch (e) {
      console.error("Failed to reorder achievements:", e);
    }
  }, [locale]);

  const moveItem = (list, index, direction, reorderFn) => {
    const newList = [...list];
    const swapIndex = index + direction;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    reorderFn(newList);
  };

  const handleAddTab = async () => {
    if (!newTabLabel.trim()) return;
    setAddingTab(true);
    try {
      const res = await axios.post(
        `/${locale}/admin/about_us/tabs`,
        { label: newTabLabel },
        axiosConfig()
      );
      setTabs([...tabs, { ...res.data, content: "", carousel_images: [] }]);
      setNewTabLabel("");
    } catch (e) {
      console.error("Failed to create tab:", e);
    }
    setAddingTab(false);
  };

  const handleDeleteTab = async (id) => {
    try {
      await axios.delete(`/${locale}/admin/about_us/tabs/${id}`, axiosConfig());
      setTabs(tabs.filter((t) => t.id !== id));
    } catch (e) {
      console.error("Failed to delete tab:", e);
    }
  };

  const handleAddAchievement = async () => {
    if (!newAchLabel.trim()) return;
    setAddingAchievement(true);
    try {
      const res = await axios.post(
        `/${locale}/admin/about_us/achievements`,
        { label: newAchLabel },
        axiosConfig()
      );
      setAchievements([...achievements, { ...res.data, icon: null }]);
      setNewAchLabel("");
    } catch (e) {
      console.error("Failed to create achievement:", e);
    }
    setAddingAchievement(false);
  };

  const handleDeleteAchievement = async (id) => {
    try {
      await axios.delete(`/${locale}/admin/about_us/achievements/${id}`, axiosConfig());
      setAchievements(achievements.filter((a) => a.id !== id));
    } catch (e) {
      console.error("Failed to delete achievement:", e);
    }
  };

  const handleUpdateTab = (updatedTab) => {
    setTabs(tabs.map((t) => (t.id === updatedTab.id ? { ...t, ...updatedTab } : t)));
  };

  const handleUpdateAchievement = (updatedAch) => {
    setAchievements(achievements.map((a) => (a.id === updatedAch.id ? { ...a, ...updatedAch } : a)));
  };

  const [activeSection, setActiveSection] = useState("tabs");

  return (
    <div className="container mt-2">
      <h3>About Us Page Management</h3>

      <ul className="nav nav-tabs mt-4">
        <li className="nav-item">
          <a
            className={`nav-link ${activeSection === "tabs" ? "active" : ""}`}
            href="#"
            onClick={(e) => { e.preventDefault(); setActiveSection("tabs"); }}
          >
            Tabs
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link ${activeSection === "achievements" ? "active" : ""}`}
            href="#"
            onClick={(e) => { e.preventDefault(); setActiveSection("achievements"); }}
          >
            Achievements
          </a>
        </li>
      </ul>

      <div className="mt-3">
        {activeSection === "tabs" && (
          <>
            {tabs.map((tab, index) => (
              <TabEditor
                key={tab.id}
                tab={tab}
                onUpdate={handleUpdateTab}
                onDelete={handleDeleteTab}
                onMoveUp={() => moveItem(tabs, index, -1, reorderTabs)}
                onMoveDown={() => moveItem(tabs, index, 1, reorderTabs)}
                isFirst={index === 0}
                isLast={index === tabs.length - 1}
              />
            ))}

            <div className="card mb-4">
              <div className="card-body">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="New tab label (e.g. 2025-2030)"
                    value={newTabLabel}
                    onChange={(e) => setNewTabLabel(e.target.value)}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-success" onClick={handleAddTab} disabled={addingTab || !newTabLabel.trim()}>
                      {addingTab ? "Adding..." : "Add Tab"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeSection === "achievements" && (
          <>
            {achievements.map((ach, index) => (
              <AchievementEditor
                key={ach.id}
                achievement={ach}
                onUpdate={handleUpdateAchievement}
                onDelete={handleDeleteAchievement}
                onMoveUp={() => moveItem(achievements, index, -1, reorderAchievements)}
                onMoveDown={() => moveItem(achievements, index, 1, reorderAchievements)}
                isFirst={index === 0}
                isLast={index === achievements.length - 1}
              />
            ))}

            <div className="card mb-4">
              <div className="card-body">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="New achievement label (e.g. Championship Wins)"
                    value={newAchLabel}
                    onChange={(e) => setNewAchLabel(e.target.value)}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-success" onClick={handleAddAchievement} disabled={addingAchievement || !newAchLabel.trim()}>
                      {addingAchievement ? "Adding..." : "Add Achievement"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AboutUsSettings;
